import { Button, Drawer, Layout, Switch } from "antd/es";
import _ from "lodash";
import React, { CSSProperties, PureComponent, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import isEqual from "react-fast-compare";
import { AMAP_DEFAULT_CENTER, warnIfOutsideAmapCoverage } from "../../../../../shared/utilty/amap-coverage";
import { loadAMap } from "../../../../../shared/utilty/amap-loader";
import { gcj02ToWgs84, wgs84ToGcj02 } from "../../../../../shared/utilty/coordinate-transform";
import { ReactSystemFunctions } from "../../../../../shared/utilty/react-system-functions";
import { Icon } from "../../../icon/icon";
import { toCamelCase } from "../../../kuika-cl-model-runtimes";
import { GoogleMapsBodyWrapperProps, IMarker, SidePanel } from "../google-maps/google-maps-body-wrapper";
import { ICoords, MarkerDetailView } from "../google-maps/google-maps-item";
import {
  AmapMapType,
  IAmapLayerVisibility,
  isSatelliteMapType,
  resolveInitialMapType,
  resolveLayerVisibility,
  toggleSatelliteMapType
} from "./amap-map-type";
import { AmapAutoComplete, IAmapPlaceLike } from "./amap-search";
import "../google-maps/maps.scss";

// react-dom/client ships without a .d.ts in this workspace (see node-tree.tsx for the same
// pattern), so `createRoot` resolves as an implicitly-typed value. This local interface pins
// down exactly the two members this file relies on, letting every root we create be annotated
// concretely instead of leaking that implicit `any` around.
interface IReactDomRoot {
  render(children: ReactNode): void;
  unmount(): void;
}

interface KuikaDesignerGlobals {
  isDesignTime?: boolean;
}

interface WindowWithKuikaGlobals {
  kuika?: KuikaDesignerGlobals;
}

const isDesignTime = (): boolean => (window as unknown as WindowWithKuikaGlobals).kuika?.isDesignTime === true;

interface ICoordinatesData {
  latitude?: number;
  longitude?: number;
  accuracy?: number;
}

interface ICoordinatesResult {
  data: ICoordinatesData;
}

interface IWatchPositionDetail {
  latitude: number;
  longitude: number;
  accuracy: number;
}

/** Arbitrary data-source row: keys are open-ended (any bound field name), values are the
 * concrete primitive shapes a table cell can hold. */
export interface IMapDataRecord {
  [fieldName: string]: string | number | boolean | null | undefined;
}

interface IMapMarkerRowElementProps {
  rowPopoverRenderer?: (
    value: ICoords | undefined,
    rowData: IMapDataRecord | undefined,
    rowIndex: number | undefined
  ) => ReactNode;
}

/** Props shape of the marker child elements rendered by base-table (record + the popover
 * render-prop chain used by the group-picker drawer). */
export interface IMapMarkerChildProps {
  record?: IMapDataRecord;
  renderIndex?: number;
  rowComponent?: (props: IMapMarkerChildProps) => React.ReactElement<IMapMarkerRowElementProps>;
  value?: ICoords;
  children?: ReactNode;
}

interface IAMapMapMouseEvent {
  lnglat: AMap.LngLat;
}

interface IAMapMapStatus {
  dragEnable?: boolean;
  rotateEnable?: boolean;
  pitchEnable?: boolean;
}

// AMap.TileLayer.Traffic resolves to a `VectorTraffic | Traffic` union depending on the
// runtime's rendering mode; VectorTraffic doesn't structurally satisfy the typed `TileLayer`
// interface (it's a plain vector Layer, not a raster TileLayer), even though both expose the
// setMap()/destroy() lifecycle this file actually relies on. This minimal interface captures
// just that shared lifecycle so Traffic/Satellite/RoadNet layer instances can be held under one
// concrete (non-`any`) field type regardless of which concrete class the JSAPI returns.
interface IAmapMapLayer {
  setMap(map: AMap.Map | null): void;
}

// The plugins below (Geocoder, Driving, MarkerCluster) are loaded by amap-loader.ts but, like
// AutoComplete/PlaceSearch in amap-search.tsx, are not declared by @amap/amap-jsapi-types.
// Minimal interfaces describe exactly the constructor/method surface used here.
interface IAMapGeocoderResultItem {
  formattedAddress?: string;
}

interface IAMapGeocoderResult {
  regeocode?: IAMapGeocoderResultItem;
}

interface IAMapGeocoder {
  getAddress(location: [number, number], callback: (status: string, result: IAMapGeocoderResult) => void): void;
}

interface IAMapDrivingOptions {
  map?: AMap.Map;
}

interface IAMapDrivingSearchOptions {
  waypoints?: [number, number][];
}

interface IAMapDrivingResult {
  info?: string;
}

interface IAMapDriving {
  search(
    origin: [number, number],
    destination: [number, number],
    opts: IAMapDrivingSearchOptions,
    callback: (status: string, result: IAMapDrivingResult) => void
  ): void;
  clear(): void;
}

interface IAMapClusterPointData {
  lnglat: [number, number];
  markerElement: React.ReactElement<IMapMarkerChildProps>;
  latitude: number;
  longitude: number;
}

interface IAMapMarkerRenderContext {
  marker: AMap.Marker;
  data: IAMapClusterPointData[];
}

interface IAMapClusterRenderContext {
  marker: AMap.Marker;
  count: number;
  clusterData: IAMapClusterPointData[];
}

interface IAMapMarkerClusterOptions {
  gridSize?: number;
  renderClusterMarker?: (context: IAMapClusterRenderContext) => void;
  renderMarker?: (context: IAMapMarkerRenderContext) => void;
}

interface IAMapMarkerCluster {
  setMap(map: AMap.Map | null): void;
  setData(points: IAMapClusterPointData[]): void;
}

interface IAMapPluginNamespace {
  Geocoder: new () => IAMapGeocoder;
  Driving: new (opts?: IAMapDrivingOptions) => IAMapDriving;
  MarkerCluster: new (
    map: AMap.Map,
    points: IAMapClusterPointData[],
    opts?: IAMapMarkerClusterOptions
  ) => IAMapMarkerCluster;
}

interface ISettingToggle {
  name: string;
  isVisible: boolean;
  value: boolean;
  onChange: (value: boolean) => void;
}

export interface AmapBodyWrapperState {
  mapReady: boolean;
  draggable: boolean;
  latitude?: number;
  longitude?: number;
  zoom?: number;
  isGroupPickerDetailsOpen: boolean;
  groupPickerDetails: React.ReactElement<IMapMarkerChildProps>[];
  userCoords?: ICoords;
  rotateControl: boolean;
  cameraControl: boolean;
  hideTrafficLayer: boolean;
  isMapSettingsPanelOpen?: boolean;
  // State, not an instance field: the map type changes at runtime (drawer switch, or a changed
  // mapType prop) and componentDidUpdate has to see the previous value to re-apply the layers.
  mapType: AmapMapType;
  // The mapType prop the current state was derived from, so getDerivedStateFromProps can tell a
  // changed prop (re-derive) from a drawer switch (keep the user's choice).
  mapTypeProp?: string;
}

/**
 * Amap (AutoNavi JSAPI 2.0) equivalent of GoogleMapsBodyWrapper. Unlike google-map-react
 * (a declarative React wrapper), Amap's JSAPI is imperative: the map instance is created once
 * in componentDidMount (after loadAMap() resolves) against a plain container div, and overlays
 * (markers, clusters, the picker dot, the circle, the driving route) are created/updated
 * imperatively as props/state change instead of being expressed as JSX children.
 *
 * Coordinate rule (K2): every value this component owns/receives from Kuika (props.value,
 * props.latitude/longitude, data-marker fields, waypoint fields, geolocation results) is
 * WGS-84. Amap's map, markers, circle, geocoder and driving service all operate in GCJ-02, so
 * wgs84ToGcj02 is applied to every coordinate handed to Amap, and gcj02ToWgs84 is applied to
 * every coordinate read back from Amap before it is reported to Kuika (onChange, map clicks,
 * search results, pan/zoom center).
 */
export class AmapBodyWrapper extends PureComponent<GoogleMapsBodyWrapperProps, AmapBodyWrapperState> {
  mapsLayout: React.RefObject<HTMLDivElement> = React.createRef();

  containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  isMarkerMouseOver = false;

  private amapNs: typeof AMap | undefined;

  private map: AMap.Map | undefined;

  private markerCluster: IAMapMarkerCluster | undefined;

  // Keyed by the Amap marker, NOT by the container element: MarkerCluster re-invokes the render
  // callbacks for the same marker on pan/zoom, and a container-keyed map would keep one live React
  // root per invocation for the lifetime of the screen (unbounded roots + detached DOM).
  private markerRoots: Map<AMap.Marker, IReactDomRoot> = new Map();

  private circleOverlay: AMap.Circle | undefined;

  private pickerMarkerInstance: AMap.Marker | undefined;

  private centerDotMarkerInstance: AMap.Marker | undefined;

  private userLocationMarkerInstance: AMap.Marker | undefined;

  private userLocationMarkerRoot: IReactDomRoot | undefined;

  private trafficLayer: IAmapMapLayer | undefined;

  private satelliteLayer: IAmapMapLayer | undefined;

  private roadNetLayer: IAmapMapLayer | undefined;

  private driving: IAMapDriving | undefined;

  private geocoder: IAMapGeocoder | undefined;

  constructor(props: GoogleMapsBodyWrapperProps) {
    super(props);
    this.state = {
      mapReady: false,
      draggable: true,
      isGroupPickerDetailsOpen: false,
      groupPickerDetails: [],
      rotateControl: props.rotateControlV1 != "hide",
      cameraControl: props.cameraControl != "hide",
      hideTrafficLayer: props.hideTrafficLayer === "hide",
      mapType: resolveInitialMapType(props.mapType),
      mapTypeProp: props.mapType
    };
  }

  /**
   * Re-derives the map type when the `mapType` prop changes (design-time edit, SetValueOf) without
   * clobbering a runtime choice made through the map-settings drawer. componentDidUpdate then sees
   * the state transition and re-applies the tile layers.
   */
  static getDerivedStateFromProps(
    props: GoogleMapsBodyWrapperProps,
    state: AmapBodyWrapperState
  ): Partial<AmapBodyWrapperState> | null {
    if (props.mapType === state.mapTypeProp) return null;
    return { mapTypeProp: props.mapType, mapType: resolveInitialMapType(props.mapType) };
  }

  public static defaultProps = {
    isDataMarkerSelected: false,
    hasSearchBox: true,
    isPicker: true,
    disableDoubleClickZoom: false,
    autoCenter: false,
    dataLatField: "lat",
    dataLngField: "lng"
  };

  convertToCoordsFieldsFromLatField = (record: IMapDataRecord | undefined): number | undefined => {
    if (this.props.dataLatField === undefined || record === undefined) return undefined;
    const rawValue = record[toCamelCase(this.props.dataLatField)];
    if (rawValue === undefined) return undefined;
    return parseFloat(String(rawValue));
  };

  convertToCoordsFieldsFromLngField = (record: IMapDataRecord | undefined): number | undefined => {
    if (this.props.dataLngField === undefined || record === undefined) return undefined;
    const rawValue = record[toCamelCase(this.props.dataLngField)];
    if (rawValue === undefined) return undefined;
    return parseFloat(String(rawValue));
  };

  componentDidMount(): void {
    if (!isDesignTime()) {
      this.setupLocationTracking();
    }
    this.initializeMap();
  }

  componentWillUnmount(): void {
    window.removeEventListener("kuika-watch-position-update", this.handleLocationUpdate);
    this.markerCluster?.setMap(null);
    this.cleanupMarkerRoots();
    this.userLocationMarkerRoot?.unmount();
    this.map?.destroy();
  }

  componentDidUpdate(prevProps: Readonly<GoogleMapsBodyWrapperProps>, prevState: Readonly<AmapBodyWrapperState>): void {
    const { latitude, longitude, autoCenter } = this.props;
    const latitudeNum = parseFloat(String(latitude));
    const longitudeNum = parseFloat(String(longitude));

    if (
      autoCenter &&
      (latitude !== prevProps.latitude ||
        longitude !== prevProps.longitude ||
        (latitudeNum !== prevState.latitude && !isNaN(latitudeNum)) ||
        (longitudeNum !== prevState.longitude && !!isNaN(longitudeNum)))
    ) {
      if (latitude !== undefined && longitude !== undefined && !isNaN(Number(latitude)) && !isNaN(Number(longitude))) {
        this.setState({
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude)
        });
      }
    }

    if (this.state.latitude !== prevState.latitude || this.state.longitude !== prevState.longitude) {
      this.recenterMap();
      this.handleDrawRadius();
      // The radius filter in buildClusterPoints is relative to the map center, so markers entering
      // or leaving the circle must be re-evaluated when that center moves. Only matters when a
      // radius is configured; without one the marker set is center-independent.
      if (this.props.circleRadius) {
        this.syncMarkers();
      }
    }

    if (!isEqual(prevProps.waypoints, this.props.waypoints) || !isEqual(prevState.userCoords, this.state.userCoords)) {
      this.setWaypointsData();
    }

    if (
      prevProps.children !== this.props.children ||
      prevProps.value !== this.props.value ||
      prevProps.circleRadius !== this.props.circleRadius
    ) {
      this.syncMarkers();
      this.handleDrawRadius();
    }

    if (prevProps.value !== this.props.value) {
      this.renderPickerMarker();
    }

    if (prevState.hideTrafficLayer !== this.state.hideTrafficLayer) {
      this.applyTrafficLayer();
    }

    // Covers both sources of a map-type change: the drawer switch and a changed mapType prop
    // (re-derived in getDerivedStateFromProps).
    if (prevState.mapType !== this.state.mapType) {
      this.applyMapTypeLayers();
    }

    if (prevState.rotateControl !== this.state.rotateControl || prevState.cameraControl !== this.state.cameraControl) {
      this.applyMapStatus();
    }

    if (prevState.userCoords !== this.state.userCoords) {
      this.renderCurrentLocationMarker();
    }
  }

  private setupLocationTracking(): void {
    if (this.props.latitude !== undefined && this.props.longitude !== undefined) {
      return;
    }
    if (this.props.enableLocationTracking === true) {
      window.addEventListener("kuika-watch-position-update", this.handleLocationUpdate);
      ReactSystemFunctions.locationGetCurrentCoordinates().catch((e: unknown) => {
        console.error("Error getting current coordinates:", e);
      });
    } else {
      this.setUserCoords();
    }
  }

  private handleLocationUpdate = (event: Event): void => {
    const customEvent = event as CustomEvent<IWatchPositionDetail>;
    const { latitude, longitude, accuracy } = customEvent.detail;
    if (this.props.enableLocationTracking === true && this.props.currentLocationColor) {
      this.setState({ userCoords: { latitude, longitude, accuracy } });
    }
  };

  private async setUserCoords(): Promise<void> {
    if (isDesignTime() || !("geolocation" in navigator) || this.props.enableLocationTracking) return;
    try {
      const currentCoordinatesResult: ICoordinatesResult = await ReactSystemFunctions.locationGetCurrentCoordinates();
      const latitude = currentCoordinatesResult?.data?.latitude;
      const longitude = currentCoordinatesResult?.data?.longitude;
      const accuracy = currentCoordinatesResult?.data?.accuracy;
      if (latitude && longitude) {
        const formattedValue: ICoords | undefined = this.props.value;
        this.setState({ userCoords: { latitude, longitude, accuracy } }, () => {
          if (
            formattedValue === undefined ||
            formattedValue.latitude === undefined ||
            formattedValue.longitude === undefined ||
            formattedValue.latitude <= 0 ||
            formattedValue.longitude <= 0
          ) {
            this.setState({ latitude, longitude });
          }
        });
      }
    } catch (e) {
      console.error("Error getting current coordinates:", e);
    }
  }

  private async initializeMap(): Promise<void> {
    let amapNs: typeof AMap;
    try {
      amapNs = await loadAMap();
    } catch (error) {
      console.error("Error loading Amap JSAPI:", error);
      return;
    }

    if (!this.containerRef.current) return;
    this.amapNs = amapNs;

    const center = this.getCenter();
    const gcjCenter = wgs84ToGcj02(center.lat, center.lng);

    warnIfOutsideAmapCoverage(center.lat, center.lng, this.getZoom());

    this.map = new amapNs.Map(this.containerRef.current, {
      center: [gcjCenter.longitude, gcjCenter.latitude],
      zoom: this.getZoom(),
      dragEnable: this.state.draggable,
      doubleClickZoom: !this.props.disableDoubleClickZoom,
      defaultCursor: this.props.isPicker === true ? "crosshair" : "grab",
      rotateEnable: this.state.rotateControl,
      pitchEnable: this.state.cameraControl
    });

    this.attachMapEventListeners();
    this.applyMapTypeLayers();
    this.applyTrafficLayer();
    this.syncMarkers();
    this.handleDrawRadius();
    this.renderPickerMarker();
    this.renderCurrentLocationMarker();
    this.setWaypointsData();

    const map = this.map;
    this.setState({ mapReady: true }, () => {
      if (this.props.onGoogleApiLoaded) {
        this.props.onGoogleApiLoaded(map, amapNs);
      }
    });
  }

  private attachMapEventListeners(): void {
    if (!this.map) return;
    this.map.on("click", this.handleMapClick);
    this.map.on("moveend", this.handleMapMoveEnd);
    this.map.on("zoomend", this.handleMapMoveEnd);
  }

  private handleMapClick = (e: IAMapMapMouseEvent): void => {
    const wgs84 = gcj02ToWgs84(e.lnglat.lat, e.lnglat.lng);
    this.handleMarkerPickerclick({ lat: wgs84.latitude, lng: wgs84.longitude });
  };

  private handleMapMoveEnd = (): void => {
    if (!this.map) return;
    const centerGcj = this.map.getCenter();
    const centerWgs84 = gcj02ToWgs84(centerGcj.lat, centerGcj.lng);
    const roundedLatitude = Number(centerWgs84.latitude.toFixed(6));
    const roundedLongitude = Number(centerWgs84.longitude.toFixed(6));
    const zoom = this.map.getZoom();

    this.setState({ zoom }, () => {
      if (this.props.onChange) {
        this.props.onChange(false, { latitude: roundedLatitude, longitude: roundedLongitude });
      }
    });
  };

  private recenterMap(): void {
    if (!this.map) return;
    const gcj = wgs84ToGcj02(this.getLatitude(), this.getLongitude());
    this.map.setCenter([gcj.longitude, gcj.latitude]);
  }

  private applyMapTypeLayers(): void {
    if (!this.map || !this.amapNs) return;
    const tileLayerNs = this.amapNs.TileLayer;
    const layers: IAmapLayerVisibility = resolveLayerVisibility(this.state.mapType);

    this.satelliteLayer?.setMap(null);
    this.roadNetLayer?.setMap(null);

    if (layers.satellite) {
      if (!this.satelliteLayer) {
        this.satelliteLayer = new tileLayerNs.Satellite();
      }
      this.satelliteLayer.setMap(this.map);
    }

    if (layers.roadNet) {
      if (!this.roadNetLayer) {
        this.roadNetLayer = new tileLayerNs.RoadNet();
      }
      this.roadNetLayer.setMap(this.map);
    }
  }

  private applyTrafficLayer(): void {
    if (!this.map || !this.amapNs) return;
    if (!this.trafficLayer) {
      this.trafficLayer = new this.amapNs.TileLayer.Traffic();
    }
    if (this.state.hideTrafficLayer) {
      this.trafficLayer.setMap(null);
    } else {
      this.trafficLayer.setMap(this.map);
    }
  }

  private applyMapStatus(): void {
    if (!this.map) return;
    const status: IAMapMapStatus = {
      dragEnable: this.state.draggable,
      rotateEnable: this.state.rotateControl,
      pitchEnable: this.state.cameraControl
    };
    this.map.setStatus(status);
  }

  getCenter = (): IMarker => ({ lat: this.getLatitude(), lng: this.getLongitude() });

  getLatitude = (): number => {
    if (this.state.latitude !== undefined) {
      return this.state.latitude;
    }
    if (this.props.latitude !== undefined && !isNaN(Number(this.props.latitude))) {
      return parseFloat(this.props.latitude);
    }
    // Amap's own default, NOT the Istanbul pair google-maps-body-wrapper falls back to: that
    // one is outside Amap's mainland-China coverage and renders as an empty grid. See
    // shared/utilty/amap-coverage.ts.
    return AMAP_DEFAULT_CENTER.latitude;
  };

  getLongitude = (): number => {
    if (this.state.longitude !== undefined) {
      return this.state.longitude;
    }
    // Inherited from google-maps-body-wrapper.tsx: this checks props.latitude (not longitude)
    // for NaN. Kept identical on purpose so both providers behave the same way; see the port's
    // final report for the note on not silently fixing cross-provider behavior in one place.
    if (this.props.longitude !== undefined && !isNaN(Number(this.props.latitude))) {
      return parseFloat(this.props.longitude);
    }
    return AMAP_DEFAULT_CENTER.longitude;
  };

  getZoom = (): number => {
    if (this.state.zoom !== undefined) {
      return this.state.zoom;
    }
    if (this.props.zoom !== undefined) {
      return this.props.zoom;
    }
    return 11;
  };

  /**
   * Calculates distance between 2 coordinates by using **Haversine Formula**
   */
  calculateDistance(crd1: IMarker, crd2: IMarker): number {
    const EARTH_RADIUS = 6371;
    const dLat = this.deg2Rad(crd1.lat - crd2.lat);
    const dLng = this.deg2Rad(crd1.lng - crd2.lng);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2Rad(crd1.lat)) * Math.cos(this.deg2Rad(crd2.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS * c;
  }

  deg2Rad(dif: number): number {
    return dif * (Math.PI / 180);
  }

  calculateGroupPickerDrawerWidth(): number {
    let drawerWidth = 378;
    const { markerDetailWidth } = this.props;

    if (markerDetailWidth) {
      drawerWidth = markerDetailWidth > drawerWidth ? markerDetailWidth : drawerWidth;
      if (this.mapsLayout.current) {
        const halfWidthOfMap = this.mapsLayout.current.offsetWidth / 2;
        if (halfWidthOfMap < drawerWidth) {
          drawerWidth = halfWidthOfMap;
        }
      }
    }

    return drawerWidth + 32;
  }

  handleClickDataMarker = (marker: IMarker): void => {
    if (marker && marker.lat && marker.lng) {
      const value: ICoords = { latitude: marker.lat, longitude: marker.lng };
      this.generateAddress(marker.lat, marker.lng);
      if (this.props.onChange) {
        this.props.onChange(true, value);
        if (!this.props.autoCenter) return;
        this.setState({ latitude: marker.lat, longitude: marker.lng });
      }
    }
  };

  handleMarkerPickerclick = (marker: IMarker): void => {
    if (this.isMarkerMouseOver === false && marker && marker.lat && marker.lng && this.props.isPicker === true) {
      const value: ICoords = { latitude: marker.lat, longitude: marker.lng };
      this.generateAddress(marker.lat, marker.lng);
      if (this.props.onChange) {
        this.props.onChange(false, value);
        if (!this.props.autoCenter) return;
        this.setState({ latitude: marker.lat, longitude: marker.lng });
      }
    }
  };

  generateAddress(lat: number, lng: number): void {
    if (!this.map || !this.amapNs) return;
    if (!this.geocoder) {
      const plugins: IAMapPluginNamespace = this.amapNs as unknown as IAMapPluginNamespace;
      this.geocoder = new plugins.Geocoder();
    }
    const gcj = wgs84ToGcj02(lat, lng);
    this.geocoder.getAddress([gcj.longitude, gcj.latitude], () => {
      // Result intentionally unused: mirrors google-maps-body-wrapper.tsx, which resolves the
      // reverse-geocode call but never applies the result to component state.
    });
  }

  private setWaypointsData(): void {
    if (!this.map || !this.amapNs) return;
    const { waypointLatField, waypointLngField, waypoints: waypointsData } = this.props;

    if (!waypointLatField || !waypointLngField || !waypointsData || waypointsData.length === 0) return;

    const coordinates: IMarker[] = [];
    if (this.state.userCoords) {
      coordinates.push({ lat: this.state.userCoords.latitude, lng: this.state.userCoords.longitude });
    }

    waypointsData.forEach((wp: IMapDataRecord) => {
      const lat = wp[waypointLatField];
      const lng = wp[waypointLngField];
      const latNum = Number(lat);
      const lngNum = Number(lng);
      if (!Number.isNaN(latNum) && !Number.isNaN(lngNum) && lat !== null && lng !== null) {
        coordinates.push({ lat: latNum, lng: lngNum });
      }
    });

    if (coordinates.length < 2) return;

    const origin = coordinates[0];
    const destination = coordinates[coordinates.length - 1];
    const waypoints = coordinates.slice(1, coordinates.length - 1);

    const originGcj = wgs84ToGcj02(origin.lat, origin.lng);
    const destinationGcj = wgs84ToGcj02(destination.lat, destination.lng);
    const waypointsGcj: [number, number][] = waypoints.map((wp: IMarker) => {
      const gcj = wgs84ToGcj02(wp.lat, wp.lng);
      return [gcj.longitude, gcj.latitude];
    });

    const plugins: IAMapPluginNamespace = this.amapNs as unknown as IAMapPluginNamespace;
    if (!this.driving) {
      this.driving = new plugins.Driving({ map: this.map });
    } else {
      this.driving.clear();
    }

    this.driving.search(
      [originGcj.longitude, originGcj.latitude],
      [destinationGcj.longitude, destinationGcj.latitude],
      { waypoints: waypointsGcj },
      (status: string, result: IAMapDrivingResult) => {
        if (status !== "complete") {
          console.error("Error fetching Amap driving directions", result);
        }
      }
    );
  }

  handleMarkerInteractionMouseOver = (): void => {
    this.isMarkerMouseOver = true;
  };

  handleMarkerInteractionMouseOut = (): void => {
    this.isMarkerMouseOver = false;
  };

  selectPlace = (place: IAmapPlaceLike): void => {
    this.handleMarkerPickerclick({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      label: place.name
    });
  };

  private isSelectedMarker(latitude: number, longitude: number): boolean {
    const value = this.props.value;
    return !!value && value.latitude === latitude && value.longitude === longitude;
  }

  private renderPickerMarker(): void {
    if (!this.map || !this.amapNs) return;
    const value = this.props.value;

    if (!value || value.latitude === undefined || value.longitude === undefined) {
      if (this.pickerMarkerInstance) {
        this.map.remove(this.pickerMarkerInstance);
        this.pickerMarkerInstance = undefined;
      }
      return;
    }

    const gcj = wgs84ToGcj02(value.latitude, value.longitude);
    if (!this.pickerMarkerInstance) {
      const container = document.createElement("div");
      container.className = "marker-style";
      container.style.zIndex = "99";
      this.pickerMarkerInstance = new this.amapNs.Marker({
        map: this.map,
        position: [gcj.longitude, gcj.latitude],
        content: container,
        zIndex: 99
      });
    } else {
      this.pickerMarkerInstance.setPosition([gcj.longitude, gcj.latitude]);
    }
  }

  private renderCenterDotMarker(): void {
    if (!this.map || !this.amapNs) return;
    const { isMarkerPicker, circleRadius } = this.props;

    if (!isMarkerPicker || !circleRadius) {
      if (this.centerDotMarkerInstance) {
        this.map.remove(this.centerDotMarkerInstance);
        this.centerDotMarkerInstance = undefined;
      }
      return;
    }

    const center = this.getCenter();
    const gcj = wgs84ToGcj02(center.lat, center.lng);

    if (!this.centerDotMarkerInstance) {
      const container = document.createElement("div");
      container.style.width = "8px";
      container.style.height = "8px";
      container.style.borderRadius = "100%";
      container.style.backgroundColor = "rgba(3, 88, 255, 100)";
      container.style.transform = "translate(-50%, -50%)";
      this.centerDotMarkerInstance = new this.amapNs.Marker({
        map: this.map,
        position: [gcj.longitude, gcj.latitude],
        content: container,
        zIndex: 98
      });
    } else {
      this.centerDotMarkerInstance.setPosition([gcj.longitude, gcj.latitude]);
    }
  }

  private buildCurrentLocationIcon(currentLocationIcon: string | undefined, currentLocationColor: string): ReactNode {
    const iconStyles: CSSProperties = {
      color: currentLocationColor,
      transform: "translate(-50%, -50%)"
    };

    if (!currentLocationIcon) {
      iconStyles.width = 20;
      iconStyles.height = 20;
      iconStyles.background = currentLocationColor;
      iconStyles.borderRadius = "100%";
      return <div style={iconStyles}></div>;
    }
    return <Icon iconName={currentLocationIcon} style={iconStyles} />;
  }

  private renderCurrentLocationMarker(): void {
    if (!this.map || !this.amapNs) return;
    const { currentLocationIcon, currentLocationColor } = this.props;
    const userCoords = this.state.userCoords;

    if (isDesignTime() || !currentLocationColor || !userCoords) {
      if (this.userLocationMarkerInstance) {
        this.map.remove(this.userLocationMarkerInstance);
        this.userLocationMarkerInstance = undefined;
        this.userLocationMarkerRoot?.unmount();
        this.userLocationMarkerRoot = undefined;
      }
      return;
    }

    const gcj = wgs84ToGcj02(userCoords.latitude, userCoords.longitude);

    if (!this.userLocationMarkerInstance) {
      const container = document.createElement("div");
      const root: IReactDomRoot = createRoot(container);
      root.render(this.buildCurrentLocationIcon(currentLocationIcon, currentLocationColor));
      this.userLocationMarkerRoot = root;
      this.userLocationMarkerInstance = new this.amapNs.Marker({
        map: this.map,
        position: [gcj.longitude, gcj.latitude],
        content: container,
        zIndex: 100
      });
    } else {
      this.userLocationMarkerInstance.setPosition([gcj.longitude, gcj.latitude]);
    }
  }

  private handleDrawRadius(): void {
    if (!this.map || !this.amapNs) return;
    const circleRadius = this.props.circleRadius;

    if (!circleRadius) {
      if (this.circleOverlay) {
        this.map.remove(this.circleOverlay);
        this.circleOverlay = undefined;
      }
      this.renderCenterDotMarker();
      return;
    }

    const center = this.getCenter();
    const gcj = wgs84ToGcj02(center.lat, center.lng);

    if (!this.circleOverlay) {
      this.circleOverlay = new this.amapNs.Circle({
        center: [gcj.longitude, gcj.latitude],
        radius: circleRadius * 1000,
        strokeColor: "rgba(3, 88, 255, 100)",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "rgba(3, 88, 255, 100)",
        fillOpacity: 0.3
      });
      this.map.add(this.circleOverlay);
    } else {
      this.circleOverlay.setCenter([gcj.longitude, gcj.latitude]);
      this.circleOverlay.setRadius(circleRadius * 1000);
    }

    this.renderCenterDotMarker();
  }

  private handleCloseGroupPickerDetails(): void {
    this.setState({ isGroupPickerDetailsOpen: false, groupPickerDetails: [] });
  }

  private handleOpenGoupPickerDetails(details: React.ReactElement<IMapMarkerChildProps>[]): void {
    this.setState({ isGroupPickerDetailsOpen: true, groupPickerDetails: details });
  }

  private handleRenderGroupPickerDetails(): ReactNode[] | undefined {
    if (this.state.groupPickerDetails.length === 0) return undefined;

    const firstMarker = this.state.groupPickerDetails[0];
    const firstMarkerRowComponent = firstMarker.props.rowComponent?.(firstMarker.props);
    const firstMarkerPopupContent = firstMarkerRowComponent?.props.rowPopoverRenderer?.(
      undefined,
      firstMarker.props.record,
      firstMarker.props.renderIndex
    );

    if (firstMarkerPopupContent === undefined) return undefined;

    return this.state.groupPickerDetails.map((child: React.ReactElement<IMapMarkerChildProps>, i: number) => {
      const record: IMapDataRecord | undefined = child.props.record;
      const latitude = this.convertToCoordsFieldsFromLatField(record);
      const longitude = this.convertToCoordsFieldsFromLngField(record);
      const markerValue: ICoords = { latitude: latitude as number, longitude: longitude as number };
      const rowComponentElement = child.props.rowComponent?.(child.props);
      const content = rowComponentElement?.props.rowPopoverRenderer?.(undefined, record, child.props.renderIndex);

      return (
        <div
          key={i}
          style={{
            padding: "12px 16px",
            borderTop: i !== 0 ? "2px solid #ccc" : undefined
          }}
          onClick={(e: React.MouseEvent) => {
            this.props.onClusterElementClicked(e, record, child.props.renderIndex, markerValue);
          }}
        >
          {content}
        </div>
      );
    });
  }

  private getMarkerChildElements(): React.ReactElement<IMapMarkerChildProps>[] {
    const result: React.ReactElement<IMapMarkerChildProps>[] = [];
    React.Children.forEach(this.props.children, (child: ReactNode) => {
      if (React.isValidElement<IMapMarkerChildProps>(child) && child.props.record) {
        result.push(child);
      }
    });
    return result;
  }

  private buildClusterPoints(): IAMapClusterPointData[] {
    const points: IAMapClusterPointData[] = [];
    const radius = this.props.circleRadius;
    const center = this.getCenter();

    this.getMarkerChildElements().forEach((child: React.ReactElement<IMapMarkerChildProps>) => {
      const record = child.props.record;
      const latitude = this.convertToCoordsFieldsFromLatField(record);
      const longitude = this.convertToCoordsFieldsFromLngField(record);
      if (latitude === undefined || longitude === undefined || Number.isNaN(latitude) || Number.isNaN(longitude))
        return;

      if (radius && this.calculateDistance(center, { lat: latitude, lng: longitude }) > radius) return;

      const gcj = wgs84ToGcj02(latitude, longitude);
      points.push({
        lnglat: [gcj.longitude, gcj.latitude],
        markerElement: child,
        latitude,
        longitude
      });
    });

    return points;
  }

  private cleanupMarkerRoots(): void {
    this.markerRoots.forEach((root: IReactDomRoot) => {
      try {
        root.unmount();
      } catch (error) {
        console.warn("Error unmounting Amap marker root:", error);
      }
    });
    this.markerRoots.clear();
  }

  private syncMarkers(): void {
    if (!this.map || !this.amapNs) return;
    const points = this.buildClusterPoints();

    if (!this.markerCluster) {
      const plugins: IAMapPluginNamespace = this.amapNs as unknown as IAMapPluginNamespace;
      this.markerCluster = new plugins.MarkerCluster(this.map, points, {
        gridSize: 60,
        renderClusterMarker: this.renderClusterMarker,
        renderMarker: this.renderMarker
      });
      return;
    }

    this.cleanupMarkerRoots();
    this.markerCluster.setData(points);
  }

  /**
   * Renders `content` into a fresh container and hands it to `marker`, retiring whatever React root
   * this marker was showing before. MarkerCluster calls the render callbacks again for markers it
   * reuses, so without the unmount each pan/zoom would leak a root.
   */
  private mountMarkerContent(marker: AMap.Marker, content: ReactNode): void {
    const previousRoot: IReactDomRoot | undefined = this.markerRoots.get(marker);
    if (previousRoot) {
      try {
        previousRoot.unmount();
      } catch (error) {
        console.warn("Error unmounting Amap marker root:", error);
      }
      this.markerRoots.delete(marker);
    }

    const container: HTMLDivElement = document.createElement("div");
    const root: IReactDomRoot = createRoot(container);
    root.render(content);
    this.markerRoots.set(marker, root);
    marker.setContent(container);
  }

  private renderMarker = (context: IAMapMarkerRenderContext): void => {
    const point = context.data[0];
    if (!point) return;

    const isSelected = this.isSelectedMarker(point.latitude, point.longitude);
    this.mountMarkerContent(
      context.marker,
      <div
        style={this.getMapsItemStyle(isSelected)}
        onMouseOver={this.handleMarkerInteractionMouseOver}
        onMouseOut={this.handleMarkerInteractionMouseOut}
        onClick={() => this.handleClickDataMarker({ lat: point.latitude, lng: point.longitude })}
      >
        {React.cloneElement(point.markerElement, { value: this.props.value })}
      </div>
    );
  };

  private renderClusterMarker = (context: IAMapClusterRenderContext): void => {
    this.mountMarkerContent(
      context.marker,
      <div
        style={{ cursor: "pointer" }}
        onClick={() =>
          this.handleOpenGoupPickerDetails(context.clusterData.map((p: IAMapClusterPointData) => p.markerElement))
        }
      >
        {this.getGroupPickerElement(context.count, this.props.groupPickerIcon, this.props.groupPickerColor)}
      </div>
    );
  };

  getGroupPickerElement = (count: number, groupPickerIcon?: string, groupPickerColor?: string): ReactNode => {
    if (groupPickerIcon) {
      return (
        <span
          style={{
            display: "inline-flex",
            flexDirection: "column",
            color: groupPickerColor,
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: 12
          }}
        >
          <Icon iconName={groupPickerIcon} style={{}} />
          {count}
        </span>
      );
    }

    return (
      <span
        style={{
          position: "relative",
          width: 35,
          height: 47,
          display: "inline-flex",
          transform: "translate(-50%, -50%)"
        }}
      >
        <svg width="35" height="47" viewBox="0 0 35 47" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M14.3483 40.5309L8.21335 31.4229C7.38437 30.1922 6.80231 29.3262 6.42773 28.6429C6.0481 27.9503 5.97348 27.6013 5.98554 27.3875C6.01852 26.8023 6.30654 26.261 6.77348 25.9067C6.94413 25.7772 7.27528 25.6441 8.06179 25.5721C8.8378 25.501 9.88119 25.5 11.3651 25.5L23.6349 25.5C25.1188 25.5 26.1622 25.501 26.9382 25.5721C27.7247 25.6441 28.0559 25.7772 28.2265 25.9067C28.6935 26.261 28.9815 26.8023 29.0145 27.3875C29.0265 27.6013 28.9519 27.9503 28.5723 28.6429C28.1977 29.3262 27.6156 30.1922 26.7866 31.4229L20.6517 40.5309C19.9746 41.5362 19.504 42.2332 19.1014 42.7275C18.7005 43.2199 18.4522 43.4007 18.2626 43.4789C17.7742 43.6804 17.2258 43.6804 16.7374 43.4789C16.5478 43.4007 16.2995 43.2199 15.8986 42.7275C15.496 42.2332 15.0254 41.5362 14.3483 40.5309Z"
            stroke="#FF0000"
            strokeWidth="2"
          />
          <circle cx="17.5" cy="17" r="16" fill="#FB8684" stroke="#FF0000" strokeWidth="2" />
        </svg>
        <span
          style={{
            position: "absolute",
            top: "calc(50% - 17px)",
            width: "100%",
            textAlign: "center",
            color: "#fff",
            fontSize: "17px",
            fontWeight: "bold"
          }}
        >
          {count}
        </span>
      </span>
    );
  };

  getStyleProp = (): CSSProperties => {
    let style: CSSProperties = {};
    if (this.props.style) {
      style = _.clone(this.props.style);
    }
    if (!style.height) {
      style.height = "400px";
    }
    if (!style.width) {
      style.width = "100%";
    }
    style.display = "inline-flex";
    return style;
  };

  getMapsItemStyle = (isSelectedMarker: boolean): CSSProperties => {
    const style: CSSProperties = {
      cursor: "pointer",
      borderWidth: 0,
      position: "absolute",
      zIndex: 99,
      boxShadow: isSelectedMarker
        ? "-1px 3px 8px 5px rgb(255 255 255 / 41%), 2px 5px 16px 0px rgb(125 157 111 / 21%), 2px 1px 30px 11px rgb(255 255 255 / 42%)"
        : "",
      borderRadius: "15px",
      borderStyle: "solid"
    };
    if (!isDesignTime()) {
      style.transform = "translate(-50%, -50%)";
    }
    return style;
  };

  renderMapsMarkerChild(child: React.ReactElement<IMapMarkerChildProps>): ReactNode {
    const record = child.props.record;
    const value = this.props.value;
    let isSelectedMarker = false;

    const lat = this.convertToCoordsFieldsFromLatField(record);
    const lng = this.convertToCoordsFieldsFromLngField(record);

    if (record !== undefined && value !== undefined && value.latitude === lat && value.longitude === lng) {
      isSelectedMarker = true;
    }

    if (lat === undefined || lng === undefined || Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }

    return (
      <div
        style={this.getMapsItemStyle(isSelectedMarker)}
        onMouseOver={this.handleMarkerInteractionMouseOver}
        onMouseOut={this.handleMarkerInteractionMouseOut}
        onClick={() => this.handleClickDataMarker({ lat, lng })}
      >
        {React.cloneElement(child, { value }, child.props.children)}
      </div>
    );
  }

  getMapsContentForDesignTime = (): ReactNode => {
    return this.getMarkerChildElements().map((child: React.ReactElement<IMapMarkerChildProps>, i: number) => (
      <React.Fragment key={i}>{this.renderMapsMarkerChild(child)}</React.Fragment>
    ));
  };

  getFloatingPanel = (): ReactNode => {
    // Only settings with a real Amap behavioral analog are exposed here. Google's native
    // on-map widgets for fullscreen, street view, the scale bar and the zoom +/- buttons have
    // no JSAPI 2.0 equivalent without loading additional plugins (AMap.ToolBar / AMap.Scale)
    // that amap-loader.ts does not currently load, so those toggles are intentionally omitted
    // rather than rendered as no-ops.
    const userSelectSettings: ISettingToggle[] = [
      {
        name: "Rotate Control",
        isVisible: this.props.rotateControlV1 === "user select",
        value: this.state.rotateControl,
        onChange: (value: boolean) => {
          this.setState({ rotateControl: value });
        }
      },
      {
        name: "Camera Control",
        isVisible: this.props.cameraControl === "user select",
        value: this.state.cameraControl,
        onChange: (value: boolean) => {
          this.setState({ cameraControl: value });
        }
      },
      {
        name: "Hide Traffic Layer",
        isVisible: this.props.hideTrafficLayer === "user select",
        value: this.state.hideTrafficLayer,
        onChange: (value: boolean) => {
          this.setState({ hideTrafficLayer: value });
        }
      },
      {
        // Google's counterpart ("Map Type Control") only shows/hides Google's own on-map map-type
        // widget and lets that widget change the type. Amap has no such widget, so the switch owns
        // the map type directly: on = satellite tiles, off = base map.
        name: "Satellite View",
        isVisible: this.props.mapType === "user select",
        value: isSatelliteMapType(this.state.mapType),
        onChange: (value: boolean) => {
          this.setState({ mapType: toggleSatelliteMapType(value) });
        }
      }
    ];

    if (userSelectSettings.filter((s: ISettingToggle) => s.isVisible).length === 0) {
      return <></>;
    }

    return (
      <>
        <Button
          className="kuika_maps__settings-panel-button"
          icon={<Icon style={{}} iconName={"settings"} />}
          onClick={() => {
            this.setState({ isMapSettingsPanelOpen: true });
          }}
        />
        {this.state.isMapSettingsPanelOpen && (
          <div className="kuika_maps__settings-panel-overlay">
            <dialog className="kuika_maps__settings-panel-overlay__dialog">
              <header>
                <h2 translate="yes">Display Settings</h2>
                <button
                  onClick={() => {
                    this.setState({ isMapSettingsPanelOpen: false });
                  }}
                  draggable="false"
                  aria-label="Close dialog"
                  title="Close dialog"
                  type="button"
                >
                  <span></span>
                </button>
              </header>
              <div className="kuika_maps__settings-panel-overlay__dialog__content">
                <div style={{ display: "flex" }}>
                  <table>
                    <tbody>
                      {userSelectSettings
                        .filter((s: ISettingToggle) => s.isVisible)
                        .map((setting: ISettingToggle, index: number) => (
                          <tr key={index}>
                            <td>
                              <Switch size="small" checked={setting.value} onChange={setting.onChange} />
                            </td>
                            <td aria-label={setting.name}>{setting.name}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </dialog>
          </div>
        )}
      </>
    );
  };

  getMaps = (): ReactNode => {
    return (
      <div
        ref={this.mapsLayout}
        onContextMenu={(ev: React.MouseEvent) => {
          ev.preventDefault();
        }}
        style={this.getStyleProp()}
      >
        <div
          className="kuika_maps__top-left-panel"
          style={{ position: "absolute", zIndex: 999, left: 25, top: 10, display: "inline-flex", gap: 8 }}
        >
          {this.state.mapReady && (
            <>
              {this.getFloatingPanel()}
              <div className="input-group">
                <div className="input-group-prepend" style={{ position: "relative" }}>
                  <AmapAutoComplete
                    map={this.map}
                    maps={this.amapNs}
                    addplace={this.selectPlace}
                    hasSearchBox={this.props.hasSearchBox}
                    onChangeLocation={(location: ICoords) => {
                      this.setState({ latitude: location.latitude, longitude: location.longitude, zoom: 17 });
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        {isDesignTime() ? this.getMapForDesignTime() : this.getMapForRunTime()}
      </div>
    );
  };

  getMapForDesignTime = (): ReactNode => {
    return <div className="googleMapsMock">{this.getMapsContentForDesignTime()}</div>;
  };

  getMapForRunTime = (): ReactNode => {
    const drawerWidth = this.calculateGroupPickerDrawerWidth();
    const groupPickerDetails = this.handleRenderGroupPickerDetails();
    const isLiveLocationTrackingEnabled =
      this.props.enableLocationTracking === true && !!this.props.currentLocationColor;

    return (
      <Layout
        className={`kuika_maps${isLiveLocationTrackingEnabled ? " kuika_maps_with_live_location" : ""}`}
        style={{ position: "relative", overflow: "hidden" }}
      >
        <div ref={this.containerRef} style={{ width: "100%", height: "100%" }} />
        {groupPickerDetails && (
          <Drawer
            placement="right"
            getContainer={false}
            open={this.state.isGroupPickerDetailsOpen}
            rootStyle={{ position: "absolute" }}
            width={drawerWidth}
            onClose={() => this.handleCloseGroupPickerDetails()}
          >
            <div>{groupPickerDetails}</div>
          </Drawer>
        )}
      </Layout>
    );
  };

  render(): ReactNode {
    const showSidePanel =
      !isDesignTime() &&
      (this.props.markerDetailView === MarkerDetailView.SidePanel ||
        this.props.markerDetailView === MarkerDetailView.Drawer);

    if (showSidePanel) {
      return (
        <SidePanel
          content={this.props.activeContent}
          markerDetailView={this.props.markerDetailView}
          markerDetailPosition={this.props.markerDetailPosition}
          markerDetailHeight={this.props.markerDetailHeight}
          markerDetailWidth={this.props.markerDetailWidth}
          designClicked={this.props.designClicked}
          onClose={() => {
            if (this.props.clearActiveContent) {
              this.props.clearActiveContent();
            }
          }}
        >
          {this.getMaps()}
        </SidePanel>
      );
    }
    return this.getMaps();
  }
}
